import { z } from 'zod';
import { PAYMENT_PROVIDERS } from '../settings';
import { optionalText, paginationQuery, periodQuery } from './common';

/**
 * Pagamento online do catálogo pela conta da própria loja (Mercado Pago ou Asaas).
 * O Dezza nunca toca no dinheiro: cria a cobrança com as credenciais da loja e confirma
 * consultando o provedor.
 */

/** Formas cobradas online. Cartão e boleto abrem a página do provedor (sem dado de cartão no Dezza). */
export const ONLINE_PAYMENT_METHODS = ['pix', 'credit_card', 'boleto'] as const;
export type OnlinePaymentMethod = (typeof ONLINE_PAYMENT_METHODS)[number];

export const ONLINE_PAYMENT_METHOD_LABELS: Record<OnlinePaymentMethod, string> = {
  pix: 'Pix',
  credit_card: 'Cartão de crédito',
  boleto: 'Boleto',
};

export const CHARGE_STATUSES = ['pending', 'paid', 'failed', 'expired', 'canceled', 'refunded'] as const;
export type ChargeStatusKey = (typeof CHARGE_STATUSES)[number];

export const CHARGE_STATUS_LABELS: Record<ChargeStatusKey, string> = {
  pending: 'Aguardando pagamento',
  paid: 'Pago',
  failed: 'Recusado',
  expired: 'Expirado',
  canceled: 'Cancelado',
  refunded: 'Estornado',
};

export const GATEWAY_ENVIRONMENTS = ['sandbox', 'production'] as const;
export type GatewayEnvironmentKey = (typeof GATEWAY_ENVIRONMENTS)[number];

/** Validade do Pix: o Mercado Pago aceita no mínimo 30 minutos. */
export const PIX_EXPIRATION_MIN_MINUTES = 30;
export const PIX_EXPIRATION_MAX_MINUTES = 1440;

const methodsSchema = z
  .array(z.enum(ONLINE_PAYMENT_METHODS))
  .min(1, 'Escolha pelo menos uma forma de pagamento.')
  .max(3)
  .transform((list) => [...new Set(list)]);

/** POST /payments/gateways: conecta a conta da loja (as credenciais são verificadas no provedor antes de salvar). */
export const paymentGatewayConnectSchema = z
  .object({
    provider: z.enum(PAYMENT_PROVIDERS),
    environment: z.enum(GATEWAY_ENVIRONMENTS).default('production'),
    /** Simulador de pagamentos (só fora de produção): testa o fluxo inteiro sem conta real. */
    simulator: z.boolean().default(false),
    credentials: z
      .object({
        /** Mercado Pago: Access Token (APP_USR-... em produção, TEST-... em teste). */
        accessToken: optionalText(500),
        /** Mercado Pago: Public Key (não é segredo). */
        publicKey: optionalText(200),
        /** Mercado Pago: assinatura secreta do webhook (Suas integrações > Webhooks). */
        webhookSecret: optionalText(200),
        /** Asaas: chave de API ($aact_...). */
        apiKey: optionalText(500),
      })
      .default({ accessToken: null, publicKey: null, webhookSecret: null, apiKey: null }),
    methods: methodsSchema.optional(),
    pixExpirationMinutes: z.number().int().min(PIX_EXPIRATION_MIN_MINUTES).max(PIX_EXPIRATION_MAX_MINUTES).optional(),
    maxInstallments: z.number().int().min(1).max(12).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.simulator) return;
    if (v.provider === 'mercadopago' && !v.credentials.accessToken) {
      ctx.addIssue({ code: 'custom', path: ['credentials', 'accessToken'], message: 'Cole o Access Token da sua conta do Mercado Pago.' });
    }
    if (v.provider === 'asaas' && !v.credentials.apiKey) {
      ctx.addIssue({ code: 'custom', path: ['credentials', 'apiKey'], message: 'Cole a chave de API da sua conta do Asaas.' });
    }
  });
export type PaymentGatewayConnectInput = z.infer<typeof paymentGatewayConnectSchema>;

/** PATCH /payments/gateways/:id (todos os campos opcionais). */
export const paymentGatewayUpdateSchema = z
  .object({
    methods: methodsSchema,
    pixExpirationMinutes: z.number().int().min(PIX_EXPIRATION_MIN_MINUTES).max(PIX_EXPIRATION_MAX_MINUTES),
    maxInstallments: z.number().int().min(1).max(12),
    isActive: z.boolean(),
    publicKey: optionalText(200),
    /** Mercado Pago: assinatura secreta do webhook. null apaga (para de validar a assinatura). */
    webhookSecret: optionalText(200),
    /** Gera uma nova URL secreta de webhook (no Asaas o webhook é recadastrado sozinho). */
    rotateWebhook: z.boolean(),
    /** Troca as credenciais (verificadas no provedor antes de salvar). */
    credentials: z.object({ accessToken: optionalText(500), apiKey: optionalText(500) }),
  })
  .partial();
export type PaymentGatewayUpdateInput = z.infer<typeof paymentGatewayUpdateSchema>;

/** GET /payments/charges */
export const paymentChargesQuery = paginationQuery.extend({
  ...periodQuery.shape,
  status: z.enum(CHARGE_STATUSES).optional(),
  method: z.enum(ONLINE_PAYMENT_METHODS).optional(),
  provider: z.enum(PAYMENT_PROVIDERS).optional(),
  /** Cobranças de um pedido (ignora o período). */
  orderId: z.string().uuid().optional(),
  /** Número do pedido ou nome do cliente. */
  q: z.string().trim().max(80).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
});
export type PaymentChargesQuery = z.infer<typeof paymentChargesQuery>;

/** POST /payments/charges/:id/refund */
export const paymentRefundSchema = z.object({
  reason: optionalText(200),
});
export type PaymentRefundInput = z.infer<typeof paymentRefundSchema>;

/**
 * POST /public/orders/:token/pay: gera (ou devolve) a cobrança online do pedido.
 * O valor é sempre calculado no servidor (total do pedido menos o que já foi pago).
 * E-mail e CPF/CNPJ completam o cadastro quando o provedor exige e o pedido não tem.
 */
export const publicPaySchema = z.object({
  method: z.enum(ONLINE_PAYMENT_METHODS, { message: 'Escolha Pix, cartão de crédito ou boleto.' }),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('E-mail inválido')
    .max(254)
    .nullish()
    .or(z.literal('').transform(() => null)),
  document: optionalText(20),
});
export type PublicPayInput = z.infer<typeof publicPaySchema>;
